import * as Sentry from '@sentry/react';
import React, { useEffect } from 'react';
import { KPI_METRICS_API_KEY } from '~/config/environment';
import { m } from '~/paraglide/messages.js';

async function logError(error: any, info?: any) {
  console.error('Logging error:', error, info);
  Sentry.captureException(error);

  await fetch(
    'https://ops-hub-api.bloquenetwork.workers.dev/api/kpi-metrics/register-error',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': KPI_METRICS_API_KEY || '',
      },
      body: JSON.stringify({
        app: 'copilot-app',
        message: error?.message || 'unknown error',
        metadata: {
          stack: error?.stack || null,
          info: info || null,
          error: error || null,
        },
      }),
    },
  ).catch(() => {});
}

type Props = {
  children: React.ReactNode;
  fallback?: React.ReactNode;
};

type State = {
  hasError: boolean;
};

export class GlobalErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: any) {
    return { hasError: true };
  }

  componentDidCatch(error: any, info: any) {
    logError(error, info);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || <h1>{m['error_boundary.title']()}</h1>;
    }
    return this.props.children;
  }
}

export function GlobalErrorHandler({ children, fallback }: Props) {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      logError(event.error || event.message);
    };

    const handlePromiseRejection = (event: PromiseRejectionEvent) => {
      logError(event.reason);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handlePromiseRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handlePromiseRejection);
    };
  }, []);

  return (
    <GlobalErrorBoundary fallback={fallback}>{children}</GlobalErrorBoundary>
  );
}
